import { useEffect, useRef, useState } from 'react';
import { parseCopilotAccessMap } from '../../../utils/copilotAccessMap';

export interface TileAgentHandoff {
  accessPointKey: string;
  isEnabled: boolean;
  serviceName: string;
  agentName: string;
  portalName: string;
  portalUrl: string;
  welcomeMessages: string[];
}

export type HandoffState =
  | { status: 'loading' | 'legacy' }
  | { status: 'error'; accessDenied?: boolean }
  | { status: 'ready'; config: TileAgentHandoff };

class CopilotAccessDeniedError extends Error {}

export function readConfiguration(payload: unknown, tileKey: string, requireAccess = false): HandoffState {
  const entry = parseCopilotAccessMap(payload)[tileKey.trim().toLowerCase()];
  if (!entry) {
    if (requireAccess) throw new CopilotAccessDeniedError('Copilot access is unavailable for this tile.');
    return { status: 'legacy' };
  }
  if (!entry.hasAccess) throw new CopilotAccessDeniedError('Copilot access is unavailable for this tile.');
  if (entry.handoffSettingsUnavailable) throw new Error('Handoff presentation settings are unavailable.');
  const row = entry.handoff;
  if (row === undefined || row === null) {
    if (entry.handoffConfigured) throw new Error('Handoff configuration is unavailable.');
    return { status: 'legacy' };
  }
  if (typeof row !== 'object' || Array.isArray(row))
    throw new Error('Invalid handoff configuration.');
  if ('error' in row && row.error) throw new Error('Handoff configuration is unavailable.');
  if (!('isEnabled' in row)) throw new Error('Invalid handoff enablement.');
  if (typeof row.isEnabled !== 'boolean') throw new Error('Invalid handoff enablement.');
  if (!row.isEnabled) return { status: 'legacy' };
  if (!('accessPointKey' in row && 'serviceName' in row
      && 'agentName' in row && 'portalName' in row && 'portalUrl' in row && 'welcomeMessages' in row))
    throw new Error('Incomplete handoff configuration.');
  const { accessPointKey, serviceName, agentName, portalName, portalUrl, welcomeMessages } = row;
  if (typeof accessPointKey !== 'string' || accessPointKey.trim().toLowerCase() !== tileKey.trim().toLowerCase()
      || typeof serviceName !== 'string' || !serviceName.trim()
      || typeof agentName !== 'string' || !agentName.trim()
      || typeof portalName !== 'string' || !portalName.trim()
      || typeof portalUrl !== 'string' || /[\\\u0000-\u0020\u007f-\u009f]/.test(portalUrl))
    throw new Error('Incomplete handoff configuration.');
  if (!Array.isArray(welcomeMessages) || welcomeMessages.length !== 2
      || !welcomeMessages.every((message): message is string => typeof message === 'string'
        && Boolean(message.trim())
        && !/[{}]/.test(message.replace(/\{(?:UserName|ServiceName|AgentName|PortalName)\}/g, ''))))
    throw new Error('Invalid shared handoff welcome.');
  const url = new URL(portalUrl);
  const host = url.hostname.toLowerCase();
  if (url.protocol !== 'https:' || url.username || url.password || url.port
      || host === 'localhost' || host.endsWith('.localhost') || host.includes(':')
      || !/^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/i.test(host) || /^[\d.]+$/.test(host))
    throw new Error('Unsafe handoff destination.');
  const forbidden = new Set([
    'access_token', 'refresh_token', 'id_token', 'token', 'code', 'password', 'secret', 'sig', 'api_key', 'apikey'
  ]);
  const parameters = [...Array.from(url.searchParams.keys()),
    ...Array.from(new URLSearchParams(url.hash.slice(1)).keys())];
  if (parameters.some(key => forbidden.has(key.toLowerCase())))
    throw new Error('Handoff destination must not include credentials or OAuth artifacts.');
  return { status: 'ready', config: {
    accessPointKey, serviceName, agentName, portalName, portalUrl, welcomeMessages, isEnabled: true
  } };
}

export function useTileAgentHandoff(
  tileName: string | undefined, requestVersion: number | string, load: () => Promise<unknown>
): HandoffState {
  const tileKey = tileName?.trim().toLowerCase() || '';
  const identity = `${tileKey}:${requestVersion}`;
  const [snapshot, setSnapshot] = useState<{ identity: string; state: HandoffState }>();
  const authorizedTile = useRef<string>();
  useEffect(() => {
    if (!tileKey) return undefined;
    if (authorizedTile.current !== tileKey) authorizedTile.current = undefined;
    let active = true;
    let inFlight = false;
    const refresh = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const state = readConfiguration(await load(), tileKey, authorizedTile.current === tileKey);
        if (active) {
          if (state.status === 'ready') authorizedTile.current = tileKey;
          setSnapshot({ identity, state });
        }
      } catch (error) {
        console.error('Tile handoff configuration could not be loaded.');
        if (active) setSnapshot({ identity, state: {
          status: 'error', ...(error instanceof CopilotAccessDeniedError && { accessDenied: true })
        } });
      } finally {
        inFlight = false;
      }
    };
    void refresh();
    const interval = window.setInterval(() => { void refresh(); }, 30000);
    return () => { active = false; window.clearInterval(interval); };
  }, [identity, tileKey, load]);
  if (!tileKey) return { status: 'legacy' };
  return snapshot?.identity === identity ? snapshot.state : { status: 'loading' };
}
// Generated by Copilot | 2026-10-26 | 75 lines added | Auto | 1321277 bounded refresh and stale-response isolation | Time Taken 6 min
// Generated by Copilot | 2026-10-26 | 31 lines added/updated/removed | Auto | 1322441 consume selected nested handoff from existing access-map response | Time Taken 6 min
// Generated by Copilot | 2026-10-27 | 6 lines added/updated/removed | Auto | 1322441 consume environment-free configuration while retaining selected-tile validation | Time Taken 2 min
// Generated by Copilot | 2026-10-28 | 32 lines added/updated/removed | Auto | 1323031 validate runtime welcome tokens and retain public destination restrictions after SQL cutover | Time Taken 10 min
// Generated by Copilot | 2026-10-29 | 33 lines added/updated/removed | Auto | Distinguish presentation failure from explicit or removed access after an authorized handoff | Time Taken 8 min
